import {SerializedStyles} from '@emotion/react';
import {css, useTheme, Popover, PopoverOrigin, Popper, PopperPlacementType} from '@mui/material';
import React from 'react';

import {ButtonType, DlMuiButton} from './DlMuiButton';
import {GetStylesFunction} from 'src/js/common/types';

interface OwnProps {
  openButton: JSX.Element | false;
  anchorTag: HTMLElement | undefined | null;
  onClose: () => void;
  /* offset in px, with positive to the right */
  xOffset?: number;
  /* offset in px, with positive in the direction of the popup */
  yOffset?: number;
  cancelTitle?: string;
  confirmTitle?: string;
  canConfirm?: boolean;
  onConfirm?: (event?: any) => void;
  showKeepDeleteButtons?: boolean;
  showDiscardSaveButtons?: boolean;
  /** Overrides the confirm button style (defaults: chip-red for keep/delete, chip-filled for
   * discard/save). Use 'chip2-filled' to match the filled button of the modal ConfirmDialog. */
  confirmButtonType?: ButtonType;
  contentCss?: SerializedStyles;
  contentStyle?: React.CSSProperties;
  direction?: 'down' | 'up';
  /** The card's and its arrow's background together; the default is the light grey of the app's popups. */
  background?: string;
}

type Props = OwnProps;

const TRIANGLE_BOX_WIDTH = 17;
// const TRIANGLE_BOX_RADIUS_MIN = 0.5 * TRIANGLE_BOX_WIDTH;
const TRIANGLE_BOX_RADIUS_MAX = (2 * TRIANGLE_BOX_WIDTH ** 2) ** 0.5 / 2.0;

export const PopupWrapper: React.FC<Props> = (props) => {
  const {
    openButton,
    anchorTag,
    onClose,
    xOffset = 0,
    yOffset = 0,
    showKeepDeleteButtons,
    showDiscardSaveButtons,
    confirmButtonType,
    cancelTitle,
    confirmTitle,
    canConfirm = true,
    onConfirm,
    children,
    contentCss,
    contentStyle,
    direction,
    background,
    ...restProps
  } = props;
  const theme = useTheme();
  const styles = getStyles({
    theme,
    params: {contentCss},
  });

  // direction
  let anchorOrigin: PopoverOrigin;
  let transformOrigin: PopoverOrigin;
  const paperStyle: React.CSSProperties = {};
  const triangleStyle: React.CSSProperties = background ? {background} : {};
  if (direction === 'up') {
    anchorOrigin = {
      vertical: 'top',
      horizontal: 'center',
    };
    transformOrigin = {
      vertical: 'bottom',
      horizontal: 'center',
    };
    paperStyle.transform = `translateY(${-(TRIANGLE_BOX_RADIUS_MAX + yOffset)}px)`;
    triangleStyle.transform = `translate(-50%, ${-yOffset}px) rotate(-45deg)`;
  } else {
    anchorOrigin = {
      vertical: 'bottom',
      horizontal: 'center',
    };
    transformOrigin = {
      vertical: 'top',
      horizontal: 'center',
    };
    paperStyle.marginTop = TRIANGLE_BOX_RADIUS_MAX + yOffset;
    triangleStyle.transform = `translate(-50%, ${yOffset}px) rotate(-45deg)`;
  }

  return (
    <>
      {openButton}
      <Popover
        open={Boolean(anchorTag)}
        anchorEl={anchorTag}
        onClose={onClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        PaperProps={{
          style: {
            ...paperStyle,
            overflow: 'visible',
            background: 'none',
            marginLeft: xOffset > 0 ? xOffset : undefined,
            marginRight: xOffset < 0 ? -xOffset : undefined,
            borderRadius: 12,
          },
          // elevation: 0,
        }}
        data-name='PopupWrapper'
        {...restProps}
      >
        <div css={styles.popupContent} style={background ? {background, ...contentStyle} : contentStyle}>
          {children}
          {showKeepDeleteButtons && (
            <div css={styles.yesNoWrapper}>
              <DlMuiButton type='chip' title={cancelTitle || 'common.confirmDialog.keep'} onClick={onClose} />
              <DlMuiButton
                type={confirmButtonType ?? 'chip-red'}
                title={confirmTitle || 'common.confirmDialog.delete'}
                onClick={onConfirm}
                disabled={!canConfirm}
              />
            </div>
          )}
          {showDiscardSaveButtons && (
            <div css={styles.yesNoWrapper}>
              <DlMuiButton type='chip' title={cancelTitle || 'common.confirmDialog.discard'} onClick={onClose} />
              <DlMuiButton
                type={confirmButtonType ?? 'chip-filled'}
                title={confirmTitle || 'common.confirmDialog.save'}
                onClick={onConfirm}
                disabled={!canConfirm}
              />
            </div>
          )}
        </div>
      </Popover>
      <Popper
        open={Boolean(anchorTag)}
        anchorEl={anchorTag}
        placement={anchorOrigin.vertical as PopperPlacementType}
        data-name='PopupWrapper-triangle'
      >
        <div css={styles.popupTriangle} data-direction={direction} style={triangleStyle} />
      </Popper>
    </>
  );
};

PopupWrapper.displayName = 'PopupWrapper';

const getStyles: GetStylesFunction = (options) => ({
  popupTriangle: css({
    'background': options?.theme?.palette.custom.coolGray['50'],
    'position': 'absolute',
    'width': 17,
    'height': 17,
    'top': 4, // NOTE: offset for after we rotate by 45deg
    '&[data-direction="up"]': {
      top: 'unset',
      bottom: 4, // NOTE: offset for after we rotate by 45deg
    },
  }),
  popupContent: css(
    {
      background: options?.theme?.palette.custom.coolGray['50'],
      borderRadius: 12,
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    },
    options?.params?.contentCss
  ),
  yesNoWrapper: css({
    'padding': '8px 16px 0px 16px',
    // Equal footprint for both actions without stretching either: a max-content-wide grid with two
    // '1fr' columns resolve both tracks to the wider button's natural width, so a short confirm
    // ("Ano") matches a long cancel ("Zrušit") and neither grows past what its label needs.
    'alignSelf': 'center',
    'display': 'grid',
    'gridAutoFlow': 'column',
    'gridAutoColumns': '1fr',
    'width': 'max-content',
    'maxWidth': '100%',
    'alignItems': 'center',
    'gap': 16,
    '& .MuiChip-root': {width: '100%'},
  }),
});
